
import React, {useState} from "react";
import { DialogProps } from "./DialogProps";
import {
  Button, Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List, ListItem, ListItemIcon, ListItemText,
  Radio,
  RadioGroup,
} from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import Checkbox from "@mui/material/Checkbox";
import {uniq} from "ramda";

export interface CreditPullConfirmationDialogProps extends DialogProps<void> {
  title: string;
  prompt: string;
  creditBureaus: string[];
  // "soft" forces single-select when more than one bureau is offered — 700Credit's /Request API
  // rejects multi-bureau pre-qualify (PRODUCT=PREQUALIFY).
  pullType: "hard" | "soft";
  handlePullCredit: (creditBureaus: string[]) => void;
}

const getBureauName = (abbreviatedBureau: string): string => {
  switch (abbreviatedBureau) {
    case "EQ":
      return "Equifax";
    case "TU":
      return "Transunion";
    case "EX":
      return "Experian";
    default:
      return "Error- Contact Support";
  }
};

type PickerMode = "none" | "single-prefilled" | "soft-radio" | "hard-checkbox";

export default function CreditPullConfirmationDialog(props: CreditPullConfirmationDialogProps) {
  const [selectedCreditBureaus, setSelectedCreditBureaus] = useState<string[]>(
    props.creditBureaus.length === 0 ? [] :
    props.creditBureaus.length === 1 ? props.creditBureaus :
    props.pullType === "soft" ? [props.creditBureaus[0]] :
    props.creditBureaus
  );

  const mode: PickerMode =
    props.creditBureaus.length === 0 ? "none" :
    props.creditBureaus.length === 1 ? "single-prefilled" :
    props.pullType === "soft" ? "soft-radio" : "hard-checkbox";

  const toggleBureau = (cb: string) =>
    setSelectedCreditBureaus(prevSelectedCreditBureaus => uniq(
      prevSelectedCreditBureaus.includes(cb)
        ? prevSelectedCreditBureaus.filter(b => b !== cb)
        : [...prevSelectedCreditBureaus, cb]
    ));

  return (
    <Dialog open={true} fullScreen={false}>
      <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
        {props.title}
      </DialogTitle>
      <DialogContent style={{paddingBottom: '0px'}}>
        {props.prompt}
        {mode === "soft-radio" &&
          <RadioGroup
            style={{width: '100%', paddingTop: '8px'}}
            value={selectedCreditBureaus[0] ?? ""}
            onChange={(_, value) => setSelectedCreditBureaus([value])}
          >
            {props.creditBureaus.map(cb => (
              <FormControlLabel key={cb} value={cb} control={<Radio />} label={getBureauName(cb)} />
            ))}
          </RadioGroup>
        }
        {mode === "hard-checkbox" &&
          <List style={{width: '100%', height: '100%', paddingBottom: '0px'}}>
            {props.creditBureaus.map(cb => (
              <ListItem key={cb} disablePadding onClick={() => toggleBureau(cb)}>
                <ListItemButton dense>
                  <ListItemIcon>
                    <Checkbox edge="start" checked={selectedCreditBureaus.includes(cb)} />
                  </ListItemIcon>
                  <ListItemText primary={getBureauName(cb)} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        }
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={props.closeDialog} color="primary">
          Cancel
        </Button>
        {mode === "single-prefilled" &&
          <Button onClick={() => props.handlePullCredit([props.creditBureaus[0]])} color="primary">
            {`Pull with ${getBureauName(props.creditBureaus[0])}`}
          </Button>
        }
        {(mode === "soft-radio" || mode === "hard-checkbox") &&
          <Button
            onClick={() => props.handlePullCredit(selectedCreditBureaus)}
            color="primary"
            disabled={selectedCreditBureaus.length === 0}
          >
            Pull Credit
          </Button>
        }
      </DialogActions>
    </Dialog>
  );
}
